import { useGlobalStore } from 'libs/lead-capture-form/state/globalStore';
import { ENTITY_TYPE } from 'libs/lead-capture-form/utils/constants';
import { Enum, EVENT_STATUS } from 'libs/lead-capture-form/utils/types';
import { memo, RefObject, useEffect, useRef, useState } from 'react';

import { PlayerAssetUrls } from '@vimeo/core/AppTypes';

import InteractionTool from '../interaction-tool';
import EmbedPlaylistApp from './containers/EmbedPlaylistApp';
import { useRLEWithLead } from './helper/useRLEWithLead';

declare global {
  interface Window {
    PlayerManager: {
      getPlayer: (playerElementRef: HTMLDivElement, initialized: boolean) => PlayerRef;
      run: (playerAssetUrls: PlayerAssetUrls) => void;
    };
  }
}

interface Clip {
  config: string;
  id: number;
}

interface Props {
  clips: Clip[];
  isRLE: boolean;
  liveEvent: {
    show_start_time: boolean;
  };
  playlistData: {
    showPlaylist: boolean;
    title: string;
  };
  eventStatus: Enum<typeof EVENT_STATUS>;
  isEmbeddedInAppearancePage?: boolean;
}

interface PlayerRef {
  loadVideo: (configUrl: string) => void;
  ready: () => Promise<void>;
}

/**
 * PlayerManager doesn't do its job sometimes, so we need to retry failures
 */
const getPlayerWithRetry = (
  playerElementRef: RefObject<HTMLDivElement>,
  callback: (player: PlayerRef) => void,
) => {
  if (!playerElementRef.current) {
    setTimeout(getPlayerWithRetry.bind(null, playerElementRef, callback), 20);
    return;
  }

  const player = window.PlayerManager.getPlayer(playerElementRef.current, false);

  if (!player) {
    setTimeout(getPlayerWithRetry.bind(null, playerElementRef, callback), 20);
    return;
  }

  callback(player);
};

const Player = (props: Props) => {
  const playerElementRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<PlayerRef | null>(null);
  const [playerLoaded, setPlayerLoaded] = useState(false);
  const { clips, isRLE, liveEvent, playlistData, eventStatus } = props;
  const { clipToPlay: clipToPlayWithLead } = useRLEWithLead();
  const { configUrl: configUrlWithLead } = clipToPlayWithLead || {};
  const entityType = useGlobalStore((store) => store.entityType);
  const hasLeadCaptureFlow = useGlobalStore((store) => store.hasNewLeadCaptureFlow);

  // Hide the playlist UI but mount the component to make polling available for single clip RLE's
  // Hide playlist UI if RLE embed setting explicitly says so
  const hidePlaylistUI = !playlistData.showPlaylist;

  useEffect(() => {
    function setPlayerElementRef(playerElementReference: { current: HTMLDivElement | null }) {
      const playerElement = document.querySelector('.js-player');
      if (!playerElementReference.current && playerElement) {
        playerElementReference.current = playerElement as HTMLDivElement;
      } else {
        setTimeout(setPlayerElementRef.bind(null, playerElementReference), 20);
      }
    }

    setPlayerElementRef(playerElementRef);
  }, []);

  useEffect(() => {
    let timeout: number | undefined = undefined;
    function runPlayerManager() {
      if (playerElementRef.current && window.PlayerManager) {
        /**
         * Why the logic?
         *
         * In the PHP side, when the EmbedNextController is hit, the player dom is initialized with the configUrl attached to it in a data attribute called 'data-config-url'.
         * Now when the FE is rendered, the player dom is already there and the PlayerManager.run() will load the player with the config url present in 'data-config-url'.
         * But in case of events with consolidated gate, the default config url does not contain the leadUuid, so we have to load the player with the config url present in the clipToPlay which contains the leadUuid.
         * So, we first set the config url in the player dom and then run the PlayerManager.run() to load the player with the correct config url.
         *
         * What was happening earlier?
         *
         * The player was loading first with the default config url which does not have leadUuid and then we were loading the player again with the correct config url with leadUuid, which results in two config call which results in issues at player backend side.
         */
        if (
          entityType === ENTITY_TYPE.EVENT &&
          hasLeadCaptureFlow &&
          eventStatus !== EVENT_STATUS.ENDED
        ) {
          if (configUrlWithLead) {
            playerElementRef.current.setAttribute('data-config-url', configUrlWithLead);
            window.PlayerManager.run(window.playerAssetUrls);
          }
        } else {
          window.PlayerManager.run(window.playerAssetUrls);
        }
      } else {
        timeout = setTimeout(runPlayerManager, 20);
      }
    }
    runPlayerManager();

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configUrlWithLead, entityType, hasLeadCaptureFlow]);

  useEffect(() => {
    getPlayerWithRetry(playerElementRef, (player) => {
      playerRef.current = player;
      player
        .ready()
        .then(() => {
          if (playerLoaded === false) {
            setPlayerLoaded(true);
          }
        })
        .catch((err: Error) => console.error('App init failed:', err));
    });
  }, [playerLoaded]);

  useEffect(() => {
    document.title = playlistData.title;
  }, [playlistData.title]);

  return playerLoaded ? (
    <>
      <EmbedPlaylistApp
        playlistData={playlistData}
        clips={clips}
        isRLE={isRLE}
        liveEvent={liveEvent}
        player={playerRef.current}
        playerEl={playerElementRef.current}
        hidePlaylistUI={hidePlaylistUI}
      />

      <InteractionTool />
    </>
  ) : null;
};

export default memo(Player);
